import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Breadcrumbs from 'components/Breadcrumbs'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { ROUTES } from 'constants/routes'
import { useTranslation } from 'react-i18next'
import { CreateTableState, List, Typography } from 'gu-ui-kit'
import { useParams } from 'react-router'
import { ReactComponent as NoDataImage } from 'images/illustration/no-data.svg'
import NETWORKS_VK_ADS_V1 from 'api/networks/vk-ads/v1'
import omit from 'lodash/omit'
import intersectionWith from 'lodash/intersectionWith'

import {
  DROP_ALL_DATA,
  DROP_ALL_ITEMS,
  DROP_DATA,
  FETCH,
  RENAME,
  SET_FILTERS,
  SET_PAGE,
  SET_RESOURCE,
  SET_SELECTION,
  SET_SORTING,
} from 'modules/Services/CampaignsEditor/types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import FilterPanel from 'components/Filters/FilterPanel'
import ActionBar from 'modules/Services/CampaignsEditor/subcomponents/ActionBar'
import styles from 'modules/Services/CampaignsEditor/campaigns-editor.module.scss'
import GridSpinner from 'components/UI/GridSpinner'
import Pagination from 'modules/Services/CampaignsEditor/subcomponents/Pagination'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import filtersConfigs from 'modules/Services/CampaignsEditor/filtersConfigs'
import tableConfigs from 'modules/Services/CampaignsEditor/tableConfigs'
import { useCancelToken } from 'utils/hooks/useCancelToken'
import EmptyResults from 'components/EmptyResults'
import { isEmpty } from 'lodash'
import RESOURCES from 'modules/Services/CampaignsEditor/resourcesMap'
import { useHistory } from 'react-router-dom'
import qs from 'qs'
import clsx from 'clsx'
import { Helmet } from 'react-helmet'
import { uniqWith } from 'lodash/array'

const CampaignsEditor = () => {
  const pathGen = useGeneratePathI18n()
  const { networkType, resource } = useParams()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [lsFilters, setLsFilters] = useLocalStorage('campaignsEditorFilters')
  const [columnVisibility, setColumnVisibility] = useState({})
  const { cancelableDispatch, cancelPreviousRequest } = useCancelToken()
  const filterConfig = useMemo(() => filtersConfigs(resource), [resource])
  const history = useHistory()

  useEffect(() => dispatch({ type: SET_RESOURCE, payload: resource }), [resource])

  useEffect(() => {
    return () => {
      dispatch({ type: DROP_ALL_DATA })
    }
  }, [])

  const {
    loading,
    pagination = {},
    items = [],
    summary = {},
    currentListSelectionIds = [],
    sorting,
    campaignsSelectionIds,
    adGroupsSelectionIds,
  } = useSelector(
    (state) => ({
      loading: state['services/campaigns-editor'].loading,
      pagination: state['services/campaigns-editor']?.[resource]?.pagination,
      items: state['services/campaigns-editor']?.[resource]?.items,
      summary: state['services/campaigns-editor']?.[resource]?.summary,
      currentListSelectionIds: state['services/campaigns-editor']?.[resource].selection.ids,
      sorting: state['services/campaigns-editor']?.[resource].sorting,
      campaignsSelectionIds: state['services/campaigns-editor'].campaigns.selection.ids,
      adGroupsSelectionIds: state['services/campaigns-editor']['ad-groups'].selection.ids,
    }),
    shallowEqual,
  )

  const applyName = useCallback(
    (id, newValue) => dispatch({ type: RENAME, payload: { id, name: newValue } }),
    [],
  )

  const selectionIdsRef = useRef(currentListSelectionIds)
  selectionIdsRef.current = currentListSelectionIds
  const sortingRef = useRef(sorting)
  sortingRef.current = sorting

  const setRowSelection = useCallback(
    (selection) => {
      dispatch({
        type: SET_SELECTION,
        payload: { resource, ids: selection(selectionIdsRef.current) },
      })
    },
    [resource],
  )

  const handleSetSorting = useCallback((newSorting) => {
    dispatch({ type: SET_SORTING, payload: newSorting(sortingRef.current) })
  }, [])

  const changeResource = useCallback(
    (resourceName) => {
      history.replace(
        pathGen(RESOURCES[resourceName].route, { networkType, resource: resourceName }),
      )
    },
    [networkType, pathGen, history],
  )

  const tableConfig = useMemo(
    () =>
      tableConfigs(resource)(
        {
          summary,
          pagination,
          applyName,
          setColumnVisibility,
          setRowSelection,
          setSorting: handleSetSorting,
          changeResource,
        },
        t,
      ),
    [resource, summary, pagination, applyName, setColumnVisibility, setRowSelection, handleSetSorting, changeResource, t],
  )

  const tableState = useMemo(
    () =>
      CreateTableState(
        {
          ...tableConfig,
          state: {
            rowSelection: currentListSelectionIds,
            columnVisibility,
            sorting,
          },
        },
        items,
      ),
    [tableConfig, currentListSelectionIds, columnVisibility, sorting, items],
  )

  const filterManager = useFiltersManager({
    filters: useSelector((state) => state['services/campaigns-editor'].filters),
    config: filterConfig,
    setAction: SET_FILTERS,
    additionalFilterState: lsFilters?.[resource] ?? {},
  })

  useEffect(() => {
    NETWORKS_VK_ADS_V1.fetchAccounts({}).then(({ response, error }) =>
      filterManager.updateFilterConfigurationByKey({
        key: 'agency_client_external_ids',
        val: {
          options: (!error ? response.data.items : []).map(({ name, external_id }) => ({
            value: external_id,
            label: name,
          })),
        },
      }),
    )
  }, [])

  const fetchData = useCallback(() => {
    cancelPreviousRequest()
    cancelableDispatch({
      type: FETCH,
    })
  }, [cancelPreviousRequest])

  useEffect(() => {
    if (!filterManager.filters) return

    setLsFilters({
      [resource]: filterManager.filters,
    })

    dispatch({ type: DROP_DATA })
    fetchData()
  }, [sorting])

  useEffect(() => {
    if (!filterManager.filters) return

    setLsFilters({
      [resource]: filterManager.filters,
    })

    dispatch({ type: DROP_ALL_ITEMS })
    fetchData()
  }, [filterManager.filters])

  useEffect(() => {
    items.length === 0 && filterManager.filters && fetchData()
    history.replace({
      pathname: global.location.pathname,
      search: filterManager.params
        ? qs.stringify(filterManager.params, {
            arrayFormat: 'brackets',
          })
        : '',
    })
  }, [resource])

  const handleChangePage = useCallback(() => {
    if (!loading) {
      dispatch({ type: SET_PAGE, payload: pagination.page + 1 })
      fetchData()
    }
  }, [pagination, loading])

  useEffect(() => {
    if (resource === 'ad-groups') {
      dispatch({ type: DROP_DATA })
      fetchData()
    }
  }, [campaignsSelectionIds])

  useEffect(() => {
    if (resource === 'banners') {
      dispatch({ type: DROP_DATA })
      fetchData()
    }
  }, [adGroupsSelectionIds, campaignsSelectionIds])

  const selectedRows = useMemo(
    () =>
      intersectionWith(
        items,
        Object.keys(currentListSelectionIds).map((id) => +id),
        (item, id) => item.external_id === id,
      ),
    [currentListSelectionIds],
  )

  const selectedStatuses = useMemo(
    () => uniqWith(selectedRows, 'status').map((row) => row.status),
    [selectedRows],
  )

  return (
    <div>
      <Helmet defer={false}>
        <title>
          {t('campaigns_editor::title')}
          {' — '}
          {t(`campaigns_editor::resources.${resource}`)}
        </title>
      </Helmet>
      {!resource ? (
        <GridSpinner />
      ) : (
        <>
          <Breadcrumbs
            links={[
              {
                title: t('services::heading'),
                to: pathGen(ROUTES.SERVICES),
              },
              {
                title: t(`services::networks.${networkType}`),
                to: pathGen(ROUTES.SERVICES_NETWORK, {
                  networkType,
                }),
              },
            ]}
          />
          <Typography
            variant={'Header-60'}
            className={'margin-bottom-40'}
            component={'h1'}
          >
            {t('campaigns_editor::title')}
          </Typography>

          <FilterPanel
            filterManager={filterManager}
            className={'margin-bottom-32'}
          />

          <ActionBar
            selectedStatuses={selectedStatuses}
            hasItems={items.length !== 0}
            resource={resource}
            className={'margin-bottom-16'}
            tableState={tableState}
          />

          <div
            className={clsx(styles['campaigns-editor__table-wrapper'], {
              [styles['campaigns-editor__table-wrapper_empty']]: items.length === 0,
            })}
          >
            {loading ? (
              <GridSpinner
                className={styles['loader']}
                width={30}
              />
            ) : items.length === 0 ? (
              <EmptyResults
                image={<NoDataImage />}
                text={
                  isEmpty(omit(filterManager.params, ['date_from', 'date_to'])) &&
                  t('campaigns_editor::empty_results.text', {
                    context: resource,
                  })
                }
              />
            ) : (
              <List
                loading={loading}
                table={tableState}
                config={{ ...tableConfig, autoResetPage: loading }}
              />
            )}
          </div>

          {!(items.length === 0 && loading) && (
            <Pagination
              loading={loading}
              data={items}
              pagination={pagination}
              handleChangePage={handleChangePage}
            />
          )}
        </>
      )}
    </div>
  )
}

CampaignsEditor.propTypes = {}

export default CampaignsEditor
